import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";

const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem("token")}`,
});

export type NewsCategory = "opening" | "closing" | "ownership" | "recruitment" | "all";
export type NewsStatus = "pending" | "approved" | "rejected";

export interface NewsSource {
    name: string;
    domain: string;
    favicon: string | null;
}

export type ScrapedImageKind = "og" | "twitter" | "jsonld" | "link" | "content" | "rss";
export type ImageScrapeOutcome = "ok" | "empty" | "blocked" | "error";

export interface ScrapedImage {
    url: string;
    kind: ScrapedImageKind;
    width: number | null;
    height: number | null;
    alt: string | null;
}

export interface LinkedEstablishment {
    _id: string;
    name: string;
    address?: { street?: string | null; city?: string | null; postcode?: string | null; region?: string | null; country?: string | null };
    operations?: { opening_date?: string | null; closing_date?: string | null };
    // Present on suggestion results from the matching API
    matchScore?: number;
    matchReasons?: string[];
    // True when the match came from a SIRET/SIREN found verbatim in the article text —
    // near-certain, surfaced distinctly from the name/city fuzzy score.
    isDirectMatch?: boolean;
    // Present when this is a confirmed `linkedEstablishment` (not a suggestion) — powers the
    // commercial CTA on the public feed's "À ne pas manquer" card.
    phone?: string | null;
    email?: string | null;
    // Rich detail — only on the /feed endpoint's confirmed `linkedEstablishment` (the
    // /dashboard news widget + "Actualité CHR" tab): the gérant card (name, age, LinkedIn,
    // portfolio size) and the establishment's registration / rating, same shape as the
    // "Lier un établissement" picker's `RichEstablishment`.
    legal_name?: string | null;
    website_url?: string | null;
    is_active?: boolean;
    registration?: RichEstablishment["registration"];
    ratings?: { google_avg: number | null; google_count: number | null };
    categories?: string[];
    subCategories?: string[];
    owners?: RichEstablishmentOwner[];
}

export interface NewsItem {
    _id: string;
    guid: string;
    title: string;
    link: string;
    published: string;
    description: string;
    image: string | null;
    imageManuallySet?: boolean;
    scrapedImages: ScrapedImage[];
    imagesScrapedAt: string | null;
    imageScrapeOutcome?: ImageScrapeOutcome | null;
    imageScrapeDetail?: string | null;
    isManual?: boolean;
    category: NewsCategory;
    city: string | null;
    departmentCode: string | null;
    status: NewsStatus;
    source: NewsSource;
    linkedEstablishment: LinkedEstablishment | null;
    franchise: { _id: string; name: string; logoUrl: string | null; imageUrl: string | null; description: string | null; website: string | null } | null;
    aiScore: number | null;
    isFranchise: boolean | null;
    isInFrance: boolean | null;
    aiScoredAt: string | null;
    suggestedEstablishment: LinkedEstablishment | null;
    suggestedEstablishmentScore: number | null;
    // TypeSafe Jev shadow scoring (crm-back services/news/jevScoring.ts). Absent / null until
    // scored; `{ error }` when the call failed.
    jev?: JevScore | null;
}

export type JevEvent =
    | "new_opening" | "upcoming_opening" | "reopening" | "takeover"
    | "closing" | "other_venue_news" | "industry_news" | "off_topic";
export type JevScope = "one_venue" | "one_chain_unit" | "several_venues" | "group_expansion" | "no_venue";

export interface JevPerson {
    name: string;
    role: "owner_founder" | "chef_employee" | "other_person" | "not_a_person";
    confidence: number;
    ownerProb: number;
}

export interface JevScore {
    score?: number;
    needsReview?: boolean;
    event?: JevEvent;
    eventConfidence?: number;
    eventProbabilities?: Partial<Record<JevEvent, number>>;
    closingProb?: number;
    scope?: JevScope;
    scopeConfidence?: number;
    venueType?: string;
    timing?: "already_open" | "announced" | "not_an_opening";
    identification?: number;
    inFrance?: number;
    franchise?: number;
    people?: JevPerson[];
    ownerName?: string | null;
    ownerProb?: number | null;
    restaurantName?: string | null;
    restaurantProb?: number | null;
    city?: string | null;
    cityProb?: number | null;
    model?: string;
    scoredAt?: string;
    error?: string;
}

export interface NewsResponse {
    items: NewsItem[];
    total: number;
    page: number;
    totalPages: number;
    categoryCounts: Record<string, number>;
    cities: string[];
}

export interface WeekStats {
    start: string;
    end: string;
    total: number;
    pending: number;
}

export interface AdminNewsResponse extends NewsResponse {
    pendingCount: number;
    weekStats: WeekStats | null;
    missingCounts: { noPhoto: number; noEstablishment: number; pickable: number; unscraped: number };
    // Pending articles per Jev quick filter, within the selected week.
    jevCounts?: { leads: number; review: number; roundups: number; closings: number; unscored: number };
    /** Pending articles per Jev event (+ `all`), within the selected week. */
    jevEventCounts?: Partial<Record<JevEvent | "all", number>>;
}

export const getRestaurantNews = async (params: {
    /** One category, or several comma-separated. */
    category?: NewsCategory | string;
    city?: string;
    departments?: string;
    hasEstablishment?: "true";
    /** Only articles whose establishment has a known gérant. */
    hasOwner?: "true";
    page?: number;
    limit?: number;
}): Promise<NewsResponse> => {
    const res = await axios.get(`${API_URL}/admin/restaurant-news/feed`, {
        headers: authHeaders(),
        params,
    });
    return res.data;
};

export const getAdminNewsArticles = async (params: {
    category?: string;
    city?: string;
    status?: "pending" | "approved" | "rejected" | "all";
    minScore?: number;
    maxScore?: number;
    isFranchise?: "true" | "false";
    isInFrance?: "true" | "false";
    hasPhoto?: "true" | "false";
    hasEstablishment?: "true" | "false";
    pickable?: "true";
    weekStart?: string;
    jevMinScore?: number;
    jevMaxScore?: number;
    jevEvent?: string;
    jevScope?: string;
    jevReview?: "true";
    jevScored?: "true" | "false";
    sort?: "published" | "jev";
    page?: number;
    limit?: number;
}): Promise<AdminNewsResponse> => {
    const res = await axios.get(`${API_URL}/admin/restaurant-news/admin-articles`, {
        headers: authHeaders(),
        params,
    });
    return res.data;
};

export const setArticleStatus = async (id: string, status: NewsStatus): Promise<NewsItem> => {
    const res = await axios.patch(
        `${API_URL}/admin/restaurant-news/${id}/status`,
        { status },
        { headers: authHeaders() }
    );
    return res.data;
};

export const setArticleFranchise = async (id: string, franchise: string | null): Promise<NewsItem> => {
    const res = await axios.patch(
        `${API_URL}/admin/restaurant-news/${id}/franchise`,
        { franchise },
        { headers: authHeaders() }
    );
    return res.data;
};

export const setArticleEstablishment = async (id: string, establishmentId: string | null): Promise<NewsItem> => {
    const res = await axios.patch(
        `${API_URL}/admin/restaurant-news/${id}/establishment`,
        { establishmentId },
        { headers: authHeaders() }
    );
    return res.data;
};

export const getEstablishmentSuggestions = async (articleId: string): Promise<LinkedEstablishment[]> => {
    const res = await axios.get(`${API_URL}/admin/restaurant-news/${articleId}/establishment-suggestions`, {
        headers: authHeaders(),
    });
    return res.data;
};

// ─── rich establishment picker (full-page "Lier un établissement" modal) ──────

export interface RichEstablishmentOwner {
    _id: string;
    name: string;
    qualite: string | null;
    role: string | null;
    age: number | null;
    linkedinUrl: string | null;
    /** The gérant's own phone / email (same validation as linkedinUrl). */
    phone?: string | null;
    email?: string | null;
    /** On the /feed only: the rep's own LinkedIn invitation to this gérant, if any. */
    linkedinInvite?: "sent" | "queued" | null;
    activeEstablishments: number;
    closedEstablishments: number;
}

export interface RichEstablishment {
    _id: string;
    name: string;
    legal_name: string | null;
    type: string | null;
    brand_group: string | null;
    primary_declared_activity: string | null;
    website_url: string | null;
    phone: string | null;
    email: string | null;
    is_active: boolean;
    address: {
        street?: string | null;
        postcode?: string | null;
        city?: string | null;
        region?: string | null;
        country?: string | null;
    } | null;
    operations: { opening_date: string | null; closing_date: string | null };
    registration: {
        siren: string | null;
        siret: string | null;
        naf_code: string | null;
        naf_description: string | null;
        legal_form: string | null;
        capital_eur: number | null;
        creation_date: string | null;
        status: "active" | "closed" | "in_liquidation" | "other" | null;
        rcs_city: string | null;
        enseigne: string | null;
        company_size_category: "TPE" | "PME" | "ETI" | "GE" | null;
        workforce_range: string | null;
    };
    ratings: { google_avg: number | null; google_count: number | null };
    franchise: { _id: string; name: string | null } | null;
    categories: string[];
    subCategories: string[];
    owners: RichEstablishmentOwner[];
    // present only on the no-query "detected matches" response
    matchScore?: number;
    matchReasons?: string[];
    isDirectMatch?: boolean;
}

// Why the article-body founder scrape found nothing (mirrors the image scrape outcomes).
export interface FounderScrapeStatus {
    outcome: "ok" | "empty" | "blocked" | "error" | null;
    detail: string | null;
    scrapedAt: string | null;
}

export type PickerSort = "relevance" | "date" | "name";

export interface EstablishmentSearchFilters {
    owner?: string;
    name?: string;
    city?: string;
    reg?: string;
    includeClosed?: boolean;
    sort?: PickerSort;
    dir?: "asc" | "desc";
}

export interface EstablishmentSearchResult {
    establishments: RichEstablishment[];
    detectedNames: string[];
    detectedRestaurantNames: string[];
    founderScrape: FounderScrapeStatus;
    articleCity: string | null;
    filters: { owner: string; name: string; city: string; reg: string; includeClosed: boolean; sort: PickerSort; dir: "asc" | "desc" };
}

// No filters → the article's auto-detected matches with full detail.
// Filters → AND across owner (person name), name (establishment / enseigne), city, reg (SIREN/SIRET).
// Closed establishments are hidden unless `includeClosed`.
// `detectedNames` / `detectedRestaurantNames` — founder & venue names scraped from the article;
// `founderScrape` — body-scrape status so the picker can flag a blocked source.
export const searchArticleEstablishments = async (
    articleId: string,
    filters: EstablishmentSearchFilters = {},
    limit = 25
): Promise<EstablishmentSearchResult> => {
    const res = await axios.get(`${API_URL}/admin/restaurant-news/${articleId}/establishment-search`, {
        headers: authHeaders(),
        params: {
            owner: filters.owner || undefined,
            name: filters.name || undefined,
            city: filters.city || undefined,
            reg: filters.reg || undefined,
            includeClosed: filters.includeClosed ? "true" : undefined,
            sort: filters.sort && filters.sort !== "relevance" ? filters.sort : undefined,
            dir: filters.dir || undefined,
            limit,
        },
    });
    return res.data;
};

// (Re)scrape the article's source page for founder / manager + venue names. Safe to repeat.
export const scrapeArticleFounders = async (
    articleId: string
): Promise<{ detectedNames: string[]; detectedRestaurantNames: string[]; founderScrape: FounderScrapeStatus }> => {
    const res = await axios.post(
        `${API_URL}/admin/restaurant-news/${articleId}/scrape-founders`,
        {},
        { headers: authHeaders() }
    );
    return res.data;
};

export const dismissEstablishmentSuggestion = async (articleId: string): Promise<NewsItem> => {
    const res = await axios.post(
        `${API_URL}/admin/restaurant-news/${articleId}/dismiss-suggestion`,
        {},
        { headers: authHeaders() }
    );
    return res.data;
};

export const refreshEstablishmentSuggestion = async (articleId: string): Promise<NewsItem> => {
    const res = await axios.post(
        `${API_URL}/admin/restaurant-news/${articleId}/refresh-suggestion`,
        {},
        { headers: authHeaders() }
    );
    return res.data;
};

export const setArticleImage = async (id: string, image: string | null): Promise<NewsItem> => {
    const res = await axios.patch(
        `${API_URL}/admin/restaurant-news/${id}/image`,
        { image },
        { headers: authHeaders() }
    );
    return res.data;
};

// (Re)scrape the source page for candidate images — returns the updated article.
export const scrapeArticleImages = async (id: string): Promise<NewsItem> => {
    const res = await axios.post(
        `${API_URL}/admin/restaurant-news/${id}/scrape-images`,
        {},
        { headers: authHeaders() }
    );
    return res.data;
};

export interface ImageScrapeRunResult {
    scraped: number;
    withImages: number;
    blocked: number;
    errors: number;
    remaining: number;
}

// Kick off one batch of image-scraping over the articles that were never scraped (the CTA for
// the backlog of older articles). Call again to drain the rest.
export const runNewsImageScrape = async (limit?: number): Promise<ImageScrapeRunResult> => {
    const res = await axios.post(
        `${API_URL}/admin/restaurant-news/scrape-images/run`,
        limit ? { limit } : {},
        { headers: authHeaders() }
    );
    return res.data;
};

// Add an article by hand from its URL.
export const addManualNewsArticle = async (link: string): Promise<{ article: NewsItem; created: boolean }> => {
    const res = await axios.post(
        `${API_URL}/admin/restaurant-news/manual`,
        { link },
        { headers: authHeaders() }
    );
    return res.data;
};

export const setArticleCity = async (id: string, city: string | null): Promise<NewsItem> => {
    const res = await axios.patch(
        `${API_URL}/admin/restaurant-news/${id}/city`,
        { city },
        { headers: authHeaders() }
    );
    return res.data;
};

export const setArticleCategory = async (id: string, category: "opening" | "closing" | "ownership" | "recruitment"): Promise<NewsItem> => {
    const res = await axios.patch(
        `${API_URL}/admin/restaurant-news/${id}/category`,
        { category },
        { headers: authHeaders() }
    );
    return res.data;
};

export const getPendingNewsCount = async (): Promise<number> => {
    const res = await axios.get(`${API_URL}/admin/restaurant-news/pending-count`, {
        headers: authHeaders(),
    });
    return res.data.pendingCount;
};

export const triggerNewsFetch = async (): Promise<{ saved: number; skipped: number; citiesBackfilled: number }> => {
    const res = await axios.post(`${API_URL}/admin/restaurant-news/fetch`, {}, {
        headers: authHeaders(),
    });
    return res.data;
};

// ─── prospection email (dashboard "À ne pas manquer" CTA) ─────────────────────

export interface ProspectionVariables {
    restaurantName: string;
    city: string;
    postcode: string;
    openingDate: string;
    ownerFirstName: string;
    articleTitle: string;
    repFirstName: string;
    repFullName: string;
    repEmail: string;
    repCompany: string;
    repPhone: string;
}

export interface ProspectionContext {
    establishmentEmail: string | null;
    establishmentName: string;
    variables: ProspectionVariables;
}

export const getProspectionVariables = async (articleId: string): Promise<ProspectionContext> => {
    const res = await axios.get(`${API_URL}/admin/restaurant-news/${articleId}/prospection-variables`, {
        headers: authHeaders(),
    });
    return res.data;
};

export const sendProspectionEmail = async (
    articleId: string,
    payload: { to: string; toName?: string; subject: string; html: string; text: string }
): Promise<{ message: string; messageId?: string }> => {
    const res = await axios.post(`${API_URL}/admin/restaurant-news/${articleId}/prospection-email`, payload, {
        headers: authHeaders(),
    });
    return res.data;
};
